// Account operations of P0 (GEN-20, ADR-086). Screens call these; tests replace this module.
import { clearCaches } from '../lib/offline/cache';
import type { Language } from '../i18n';
import type { Role } from '../app/pages';
import { functionsUrl, supabase } from './supabase';

export interface Me {
  user_id: string;
  display_name: string;
  email: string;
  role: Role;
  active: boolean;
  must_change_password: boolean;
}

/** A refusal with a stable code (from the database, an Edge Function or Supabase Auth). */
export class Refusal extends Error {
  constructor(
    public readonly code: string,
    /** The database's detail for the refusal (e.g. which product already has a barcode). */
    public readonly detail: Record<string, unknown> = {},
  ) {
    super(code);
  }
}

const anonHeaders = () => ({
  apikey: import.meta.env.VITE_SUPABASE_ANON_KEY as string,
  Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY as string}`,
  'Content-Type': 'application/json',
});

async function call<T>(input: string, init: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(input, init);
  } catch {
    throw new Refusal('OFFLINE');
  }
  const body = (await res.json().catch(() => ({}))) as T & { error?: string };
  if (!res.ok || body.error) throw new Refusal(body.error ?? 'SERVER_ERROR');
  return body;
}

export interface SetupStatus {
  setUp: boolean;
  /** The business language, so signed-out screens speak it; null before the first run. */
  language: Language | null;
}

export async function setupStatus(): Promise<SetupStatus> {
  return call<SetupStatus>(`${functionsUrl}/setup-business`, { headers: anonHeaders() });
}

export interface SetupInput {
  businessName: string;
  language: Language;
  startDate: string;
  ownerName: string;
  email: string;
  password: string;
}

export async function setupBusiness(input: SetupInput): Promise<void> {
  await call(`${functionsUrl}/setup-business`, { method: 'POST', headers: anonHeaders(), body: JSON.stringify(input) });
}

export async function signIn(email: string, password: string): Promise<void> {
  let error: { message: string; status?: number | undefined; code?: string | undefined } | null;
  try {
    ({ error } = await supabase.auth.signInWithPassword({ email: email.trim().toLowerCase(), password }));
  } catch {
    throw new Refusal('OFFLINE');
  }
  if (!error) return;
  if (error.code === 'user_banned' || /banned/i.test(error.message)) throw new Refusal('ACCOUNT_DEACTIVATED');
  if (error.status === 0 || /fetch/i.test(error.message)) throw new Refusal('OFFLINE');
  throw new Refusal('INVALID_CREDENTIALS');
}

export async function signOut(): Promise<void> {
  // Nothing the Seller read stays on a phone they hand over (the unsent queue does: it is their work, ADR-128).
  await clearCaches();
  await supabase.auth.signOut();
}

export async function loadMe(): Promise<Me | null> {
  const { data, error } = await supabase
    .from('v_me')
    .select('user_id, display_name, email, role, active, must_change_password')
    .maybeSingle();
  if (error) throw new Refusal('SERVER_ERROR');
  return (data as Me | null) ?? null;
}

export function onSessionChange(listener: (signedIn: boolean) => void): () => void {
  const { data } = supabase.auth.onAuthStateChange((_event, session) => listener(Boolean(session)));
  return () => data.subscription.unsubscribe();
}

export async function hasSession(): Promise<boolean> {
  const { data } = await supabase.auth.getSession();
  return Boolean(data.session);
}

/** First sign-in (SCR-FND-08, ADR-086): replace the temporary password, then clear the flag in the database. */
export async function changePassword(newPassword: string): Promise<void> {
  let error: { message: string; code?: string | undefined } | null;
  try {
    ({ error } = await supabase.auth.updateUser({ password: newPassword }));
  } catch {
    throw new Refusal('OFFLINE');
  }
  if (error) {
    if (error.code === 'same_password' || /different from the old/i.test(error.message))
      throw new Refusal('SAME_PASSWORD');
    if (error.code === 'weak_password' || /at least/i.test(error.message)) throw new Refusal('WEAK_PASSWORD');
    throw new Refusal('SERVER_ERROR');
  }
  const { error: flagError } = await supabase.rpc('mark_password_changed');
  if (flagError) throw new Refusal(/^[A-Z_]+$/.test(flagError.message) ? flagError.message : 'SERVER_ERROR');
}
